import { type SiteSettings } from '#app/queries/site-settings.query.ts';
import { formatPhoneNumberForTelLink } from './misc';
import { ButtonData } from '#app/types/schema.ts';
import { MenuItemFragmentType, SubMenuItemFragmentType } from '#app/queries/fragements.ts';

/**
 * Resolves the URL path for a Sanity menu item or button object.
 * Handles internal, external, phone, and email links.
 * Requires siteSettings for global phone/email types.
 */
export function resolveSanityLink(
    item: ButtonData | MenuItemFragmentType | SubMenuItemFragmentType,
    // Make siteSettings required or handle null case inside
    siteSettings: SiteSettings | null | undefined,
): string | undefined {
    if (!item) return undefined;

    switch (item.linkType) {
        case 'external':
            return item.externalUrl ?? undefined;

        case 'internal': { // Use block scope for const declarations
            const linkData = item.internalLink;
            if (!linkData) return undefined;
            const slug = linkData.slug?.current; // Assumes slug has { current: string }
            switch (linkData._type) {
                case 'home': return '/';
                case 'page': return slug ? `/pages/${slug}` : undefined;
                case 'service': return slug ? `/services/${slug}` : undefined;
                case 'serviceArea': return slug ? `/service-areas/${slug}` : undefined;
                case 'blogPost': return slug ? `/blog/${slug}` : undefined;
                case 'jobListing': return slug ? `/careers/${slug}` : undefined;
                // Add other cases
                default:
                    console.warn(`Unhandled internal link type: ${linkData._type}`);
                    return undefined;
            }
        }

        case 'custom': {
            const customPath = item.customPath;
            return customPath ? `${customPath}` : undefined;
        }

        case 'phoneGlobal': {
            const globalPhone = siteSettings?.phoneNumber;
            return globalPhone ? formatPhoneNumberForTelLink(globalPhone) : undefined;
        }

        case 'emailGlobal': {
            const globalEmail = siteSettings?.emailAddress;
            return globalEmail ? `mailto:${globalEmail}` : undefined;
        }

        case 'quote': {
            // Assuming this is a specific route for quotes
            return '/get-a-quote';
        }

         // Custom phone number handling

        case 'phoneCustom': {
             // Ensure item type has customPhoneNumber property
             const customPhone = 'customPhoneNumber' in item ? item.customPhoneNumber : undefined;
             return customPhone ? formatPhoneNumberForTelLink(customPhone) : undefined;
         }

        case 'emailCustom': {
            // Ensure item type has customEmailAddress property
            const customEmail = 'customEmailAddress' in item ? item.customEmailAddress : undefined;
            return customEmail ? `mailto:${customEmail}` : undefined;
        }

        default:
            return undefined;
    }
}